import {
  TransactionStatus,
  type TransactionStatusType,
  type WorkflowTriggerDatasetSubmission,
} from "@validereinc/domain";
import { toTitleCaseString } from "@validereinc/utilities";

export const ALL_DATASET_TRANSACTION_STATUSES = Object.values(
  TransactionStatus
) as TransactionStatusType[];

/** Resolves configured dataset IDs for a dataset submission trigger. */
export const getDatasetSubmissionTriggerDatasetIds = (
  datasetId?: WorkflowTriggerDatasetSubmission["dataset_id"]
): string[] => {
  if (!datasetId) {
    return [];
  }

  if (typeof datasetId === "string") {
    return datasetId ? [datasetId] : [];
  }

  if ("$in" in datasetId && datasetId.$in.length > 0) {
    return datasetId.$in;
  }

  return [];
};

/** Parses API `dataset_id` into multi-select form values. */
export const datasetSubmissionDatasetIdToFormValues = (
  datasetId?: WorkflowTriggerDatasetSubmission["dataset_id"]
): string[] => getDatasetSubmissionTriggerDatasetIds(datasetId);

/** Converts multi-select form values to API `dataset_id` shape. */
export const formValuesToDatasetSubmissionDatasetId = (
  datasetIds: string[]
): WorkflowTriggerDatasetSubmission["dataset_id"] => {
  if (!datasetIds.length) {
    return "";
  }

  if (datasetIds.length === 1) {
    return datasetIds[0];
  }

  return { $in: datasetIds };
};

/** Key-value rows for each configured dataset. */
export const getDatasetSubmissionTriggerDatasetKeyValueRows = (
  datasetId: WorkflowTriggerDatasetSubmission["dataset_id"],
  datasetNameById: Record<string, string> = {}
): Array<{ title: string; value: string }> =>
  getDatasetSubmissionTriggerDatasetIds(datasetId).map((id) => ({
    title: "Dataset",
    value: datasetNameById[id] ?? id.slice(0, 7),
  }));

/** Resolves dataset display names for a dataset submission trigger overview. */
export const getDatasetSubmissionTriggerDatasetDisplayNames = (
  datasetId: WorkflowTriggerDatasetSubmission["dataset_id"],
  datasetNameById: Record<string, string> = {}
): string[] =>
  getDatasetSubmissionTriggerDatasetIds(datasetId).map(
    (id) => datasetNameById[id] ?? id.slice(0, 7)
  );

/**
 * Resolves configured transaction statuses for a dataset submission trigger.
 * When no filter is set, all transaction statuses apply.
 */
export const getDatasetSubmissionTriggerStatusValues = (
  statusFilter?: WorkflowTriggerDatasetSubmission["status_filter"]
): TransactionStatusType[] => {
  if (!statusFilter) {
    return ALL_DATASET_TRANSACTION_STATUSES;
  }

  if (typeof statusFilter === "string") {
    return [statusFilter];
  }

  if ("$in" in statusFilter && statusFilter.$in.length > 0) {
    return statusFilter.$in;
  }

  return ALL_DATASET_TRANSACTION_STATUSES;
};

/** Formats a transaction status for display in workflow trigger UI. */
export const formatDatasetSubmissionTriggerStatusLabel = (
  status: TransactionStatusType | string
): string => toTitleCaseString(status, { intent: "ui" });

/** Key-value rows for each applicable transaction status. */
export const getDatasetSubmissionTriggerStatusKeyValueRows = (
  statusFilter?: WorkflowTriggerDatasetSubmission["status_filter"]
): Array<{ title: string; value: string }> =>
  getDatasetSubmissionTriggerStatusValues(statusFilter).map((status) => ({
    title: formatDatasetSubmissionTriggerStatusLabel(status),
    value: "",
  }));

/** Resolves status display labels for a dataset submission trigger overview. */
export const getDatasetSubmissionTriggerStatusDisplayLabels = (
  statusFilter?: WorkflowTriggerDatasetSubmission["status_filter"]
): string[] =>
  getDatasetSubmissionTriggerStatusValues(statusFilter).map((status) =>
    formatDatasetSubmissionTriggerStatusLabel(status)
  );

export type DatasetSubmissionTriggerOverviewDetailsType = {
  datasetIdFilter: WorkflowTriggerDatasetSubmission["dataset_id"];
  statusFilter?: WorkflowTriggerDatasetSubmission["status_filter"];
};

export type DatasetSubmissionTriggerOverviewKeyValueEntryType = {
  datasetTitle: string;
  datasetDisplayNames: string[];
  statusesTitle: string;
  statusDisplayLabels: string[];
};

/**
 * Builds overview key-value entries for each configured dataset submission trigger.
 * Titles are numbered only when multiple triggers are present.
 */
export const getDatasetSubmissionTriggerOverviewKeyValueEntries = (
  triggers: DatasetSubmissionTriggerOverviewDetailsType[],
  datasetNameById: Record<string, string> = {}
): DatasetSubmissionTriggerOverviewKeyValueEntryType[] => {
  const configuredTriggers = triggers.filter(
    (trigger) =>
      getDatasetSubmissionTriggerDatasetIds(trigger.datasetIdFilter).length > 0
  );

  return configuredTriggers.map((trigger, index) => {
    const suffix = configuredTriggers.length > 1 ? ` ${index + 1}` : "";

    return {
      datasetTitle: `Dataset Submission Trigger Dataset${suffix}`,
      datasetDisplayNames: getDatasetSubmissionTriggerDatasetDisplayNames(
        trigger.datasetIdFilter,
        datasetNameById
      ),
      statusesTitle: `Dataset Submission Trigger Statuses${suffix}`,
      statusDisplayLabels: getDatasetSubmissionTriggerStatusDisplayLabels(
        trigger.statusFilter
      ),
    };
  });
};

/** Parses API `status_filter` into multi-select form values. */
export const datasetSubmissionStatusFilterToFormValues = (
  statusFilter?: WorkflowTriggerDatasetSubmission["status_filter"]
): TransactionStatusType[] => {
  if (!statusFilter) {
    return [];
  }

  if (typeof statusFilter === "string") {
    return [statusFilter];
  }

  if ("$in" in statusFilter) {
    return statusFilter.$in;
  }

  return [];
};

/** Converts multi-select form values to API `status_filter` shape. */
export const formValuesToDatasetSubmissionStatusFilter = (
  statuses: TransactionStatusType[]
): WorkflowTriggerDatasetSubmission["status_filter"] | undefined => {
  if (!statuses.length) {
    return undefined;
  }

  if (statuses.length === 1) {
    return statuses[0];
  }

  return { $in: statuses };
};

export const datasetSubmissionTransactionStatusDropdownOptions =
  ALL_DATASET_TRANSACTION_STATUSES.map((status) => ({
    value: status,
    label: formatDatasetSubmissionTriggerStatusLabel(status),
  }));
